// partnerships/partnerships.service.ts
import { api } from './api'; // Centralized API client
import { Partnership, CreatePartnershipData, UpdatePartnershipData } from '../store/partnershipsStore';
import type { ApiResponse } from '../utils/types';
import { toast } from 'sonner';

// API endpoints
const API_ENDPOINTS = {
  partnerships: '/partnerships',
  partnership: (id: string | number) => `/partnerships/${id}`,
  export: '/partnerships/export',
  stats: '/partnerships/stats',
  publicLink: '/partnerships/public-link',
  // Public endpoints (no auth)
  publicValidate: '/p/partnerships/validate',
  publicCreate: '/p/partnerships',
};

// Type guard for error with response
function isErrorWithResponse(error: unknown): error is { response?: { status?: number; data?: { message?: string } } } {
  return typeof error === 'object' && error !== null && 'response' in error;
}

// Partnerships Service
export class PartnershipsService {
  // Get all partnerships
  async getAll(filters?: { searchQuery?: string; stage?: string; priority?: string }): Promise<Partnership[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.searchQuery) params.append('q', filters.searchQuery);
      if (filters?.stage && filters.stage !== 'all') params.append('stage', filters.stage);
      if (filters?.priority && filters.priority !== 'all') params.append('priority', filters.priority);

      const queryString = params.toString();
      const url = queryString ? `${API_ENDPOINTS.partnerships}?${queryString}` : API_ENDPOINTS.partnerships;

      const response = await api.get<ApiResponse<Partnership[]>>(url);

      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch partnerships');

      return response.data.data.map(this.transformPartnership);
    } catch (error) {
      console.error('Failed to fetch partnerships:', error);
      throw new Error('Failed to fetch partnerships. Please try again.');
    }
  }

  // Get partnership by ID
  async getById(id: string | number): Promise<Partnership | null> {
    try {
      const response = await api.get<ApiResponse<Partnership>>(API_ENDPOINTS.partnership(id));
      if (!response.data.success) throw new Error(response.data.message || 'Partnership not found');
      return this.transformPartnership(response.data.data);
    } catch (error) {
      console.error('Failed to fetch partnership:', error);
      if (isErrorWithResponse(error) && error.response?.status === 404) return null;
      throw new Error('Failed to fetch partnership. Please try again.');
    }
  }

  // Create new partnership
  async create(data: CreatePartnershipData): Promise<Partnership> {
    try {
      const response = await api.post<ApiResponse<Partnership>>(API_ENDPOINTS.partnerships, data);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to create partnership');

      toast.success('Partnership created successfully');
      return this.transformPartnership(response.data.data);
    } catch (error) {
      console.error('Failed to create partnership:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to create partnership. Please try again.';
      toast.error(`${message}`);
      throw new Error(message);
    }
  }

  // Update partnership
  async update(id: string | number, data: Partial<UpdatePartnershipData>): Promise<Partnership> {
    try {
      const response = await api.patch<ApiResponse<Partnership>>(API_ENDPOINTS.partnership(id), data);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to update partnership');

      toast.success('Partnership updated successfully');
      return this.transformPartnership(response.data.data);
    } catch (error) {
      console.error('Failed to update partnership:', error);
      let message = 'Failed to update partnership. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Partnership not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(`${message}`);
      throw new Error(message);
    }
  }

  // Delete partnership
  async delete(id: string | number): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.partnership(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete partnership');

      toast.success('Partnership deleted successfully');
    } catch (error) {
      console.error('Failed to delete partnership:', error);
      let message = 'Failed to delete partnership. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Partnership not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(`${message}`);
      throw new Error(message);
    }
  }

  // Search partnerships
  async search(query: string): Promise<Partnership[]> {
    try {
      const response = await api.get<ApiResponse<Partnership[]>>(`${API_ENDPOINTS.partnerships}?q=${encodeURIComponent(query)}`);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to search partnerships');
      return response.data.data.map(this.transformPartnership);
    } catch (error) {
      console.error('Failed to search partnerships:', error);
      throw new Error('Failed to search partnerships. Please try again.');
    }
  }

  // Get partnerships by stage
  async getByStage(stage: string): Promise<Partnership[]> {
    try {
      const response = await api.get<ApiResponse<Partnership[]>>(`${API_ENDPOINTS.partnerships}?stage=${encodeURIComponent(stage)}`);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch partnerships by stage');
      return response.data.data.map(this.transformPartnership);
    } catch (error) {
      console.error('Failed to fetch partnerships by stage:', error);
      throw new Error('Failed to fetch partnerships by stage. Please try again.');
    }
  }

  // Get stats
  async getStats(): Promise<any> {
    try {
      const response = await api.get<ApiResponse<any>>(API_ENDPOINTS.stats);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch statistics');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch partnership statistics:', error);
      throw new Error('Failed to fetch partnership statistics. Please try again.');
    }
  }

  // Get pipeline stats
  async getPipelineStats(): Promise<any> {
    try {
      const response = await api.get<ApiResponse<any>>('/partnerships/pipeline-stats');
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch pipeline statistics');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch pipeline statistics:', error);
      throw new Error('Failed to fetch pipeline statistics. Please try again.');
    }
  }

  // Export data
  async export(format: 'csv' | 'json' = 'json'): Promise<Blob> {
    try {
      const response = await api.get(`${API_ENDPOINTS.export}?format=${format}`, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      console.error('Failed to export partnerships:', error);
      throw new Error('Failed to export partnerships. Please try again.');
    }
  }

  // ==================== PUBLIC LINK METHODS ====================

  // Generate public link (authenticated) - returns permanent link
  async generatePublicLink(): Promise<{ link: string }> {
    try {
      const response = await api.post<ApiResponse<{ link: string }>>(API_ENDPOINTS.publicLink);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to generate public link');

      toast.success('Public partnership link copied!');
      return response.data.data;
    } catch (error) {
      console.error('Failed to generate public link:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to generate public link. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Validate team link (no auth)
  async validatePublicTeam(teamId: string): Promise<boolean> {
    try {
      const response = await api.get<ApiResponse<{ valid: boolean }>>(`/p/partnerships/${teamId}/validate`);
      return response.data.success && response.data.data.valid;
    } catch (error) {
      console.error('Failed to validate team:', error);
      return false;
    }
  }

  // Submit public form (no auth)
  async createFromPublicForm(teamId: string, data: {
    brand: string;
    contact: string;
    email: string;
    value: number;
    deliveryDate?: string | undefined;
    notes?: string | undefined;
    tags?: string[] | undefined;
    phone?: string | undefined;
    website?: string | undefined;
    socialMedia?: { instagram?: string; twitter?: string; linkedin?: string; tiktok?: string } | undefined;
    campaignType?: string | undefined;
    campaignDescription?: string | undefined;
    deliverables?: { contentType: string; title: string; description?: string; deadline?: string }[] | undefined;
  }): Promise<Partnership> {
    try {
      const response = await api.post<ApiResponse<Partnership>>(`/p/partnerships/${teamId}`, data);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to submit partnership');

      toast.success('Partnership submitted successfully!');
      return this.transformPartnership(response.data.data);
    } catch (error) {
      console.error('Failed to submit partnership:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to submit partnership. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Helper: normalize data
  private transformPartnership(partnership: any): Partnership {
    return {
      ...partnership,
      id: partnership.id || partnership._id,
      createdAt: partnership.createdAt || new Date().toISOString(),
      lastUpdated: partnership.lastUpdated || partnership.updatedAt || new Date().toISOString(),
      tags: Array.isArray(partnership.tags) ? partnership.tags : [],
      deliverables: Array.isArray(partnership.deliverables) ? partnership.deliverables : [],
      socialMedia: partnership.socialMedia || {},
      stage: partnership.stage || 'Lead',
      priority: partnership.priority || 'medium',
      value: Number(partnership.value) || 0,
    };
  }
}

// Singleton instance
export const partnershipsService = new PartnershipsService();
